import { cn } from "lib/utils";
import { TONE_SOLID } from "shared/tones";
import { SCALE_REMARKS, formatScore, scaleRemark, scoreTone } from "./evaluationConfig";

interface ScorePickerProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
}

/**
 * 1-5 click-to-pick row with the scale word beside it; 0 = not scored yet.
 * The ½ toggle adds half a point to the picked number (3 -> 3.5). Imported scores can be any
 * decimal (3.6): then no number is lit, the value is shown beside the row, and a click replaces it.
 */
export default function ScorePicker({ label, value, onChange }: ScorePickerProps) {
  const whole = Math.floor(value);
  const isHalf = value > 0 && value % 1 === 0.5;
  const isWhole = value > 0 && value % 1 === 0;
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <span className="text-sm text-foreground w-44">{label}</span>
      <div className="flex items-center gap-3">
        <div className="flex gap-1" role="group" aria-label={label}>
          {[1, 2, 3, 4, 5].map((n) => {
            const active = isWhole ? value === n : isHalf && whole === n;
            return (
              <button
                key={n}
                type="button"
                aria-pressed={active}
                title={SCALE_REMARKS[n]}
                onClick={() => onChange(value === n ? 0 : n)}
                className={cn(
                  "w-8 h-8 rounded-lg border text-sm font-medium transition-colors",
                  active
                    ? `${TONE_SOLID[scoreTone(value)]} border-transparent`
                    : "bg-surface border-border text-muted-foreground hover:text-foreground hover:border-primary",
                )}
              >
                {n}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={isHalf}
            aria-label={`${label}: add half a point`}
            title="Add half a point (e.g. 3 becomes 3.5)"
            disabled={!(isWhole || isHalf) || value >= 5}
            onClick={() => onChange(isHalf ? whole : whole + 0.5)}
            className={cn(
              "w-8 h-8 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
              isHalf
                ? `${TONE_SOLID[scoreTone(value)]} border-transparent`
                : "bg-surface border-border text-muted-foreground hover:text-foreground hover:border-primary",
            )}
          >
            ½
          </button>
        </div>
        <span className="text-xs text-muted-foreground w-28">
          {value > 0 ? `${formatScore(value)} · ${scaleRemark(value)}` : "Not scored"}
        </span>
      </div>
    </div>
  );
}
